import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
} from '@angular/core';

import { ReportTPipe } from '../report-i18n.pipe';
import { ReportRevealDirective } from '../report-reveal.directive';
import { SubjectReportView } from '../report.model';

/**
 * Section « points forts · points à améliorer » d'une matière.
 *
 * Les deux colonnes sont les deux faces du même verdict d'acquisition —
 * `strengths` (acquises) et `gaps` (non acquises) — et rien d'autre : ni note,
 * ni pourcentage. Une compétence n'en a pas, et en afficher un ici reviendrait
 * à mélanger les deux mesures du rapport.
 *
 * Les compétences non évaluées ne figurent dans aucune des deux colonnes :
 * elles ne sont ni un point fort, ni une lacune. C'est pourquoi le sous-titre
 * rapporte les deux comptes au total du référentiel plutôt que l'un à l'autre.
 */
@Component({
  selector: 's2e-report-insights',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportTPipe, ReportRevealDirective],
  templateUrl: './subject-insights.section.html',
  host: { '[style.display]': "'contents'" },
})
export class SubjectInsightsSection {
  readonly subject = input.required<SubjectReportView>();

  protected readonly strengths = computed(() => this.subject().strengths);
  protected readonly gaps = computed(() => this.subject().gaps);

  /**
   * Variables des deux sous-titres.
   *
   * `count` est un nombre nu : c'est lui qui déclenche la sélection de pluriel
   * du moteur i18n. `total` passe par la forme `{ n }` pour être mis en forme
   * dans la locale, sans peser sur le pluriel.
   */
  protected readonly strengthVars = computed(() => ({
    count: this.strengths().length,
    total: { n: this.subject().acquisition.total },
  }));

  protected readonly gapVars = computed(() => ({
    count: this.gaps().length,
    total: { n: this.subject().acquisition.total },
  }));
}
